import {
  doc,
  getDoc,
  runTransaction,
  type CollectionReference,
  type DocumentData,
} from 'firebase/firestore';
import { db } from '@/firebaseInit';

// Creating a record under an id somebody chose, rather than one Firestore
// generated.
//
// Experiences, collections, guides and partners are all keyed by a slug of their
// name (see `src/util/util_slug.ts`), which means two records named the same
// thing want the same document. `setDoc` would grant that: it writes whichever
// document the id names, existing or not, so a second "Kyoto Tea Ceremony"
// would silently replace the first — same id, so nothing in the CMS would even
// look wrong afterwards.
//
// Hence a create that is a create. The transaction below is the only form that
// actually holds: a `getDoc` followed by a `setDoc` leaves a gap between the
// question and the answer, and two admins submitting at once both find the id
// free. A transaction re-runs when the document it read has moved underneath it,
// so exactly one of the two writes lands and the other is told the id is taken.

/** Thrown by the `create*` repo functions when the id is already in use. */
export class DocumentIdTakenError extends Error {
  readonly id: string;

  constructor(id: string) {
    super(`The id "${id}" is already taken.`);
    this.name = 'DocumentIdTakenError';
    this.id = id;
  }
}

/**
 * Whether a record already exists under this id.
 *
 * For telling somebody while they are still typing. It is not what makes the id
 * unique — `createDocument` is — because by the time the form is submitted the
 * answer may have changed.
 */
export async function isDocIdTaken(
  collectionRef: CollectionReference<DocumentData>,
  id: string,
): Promise<boolean> {
  if (!id) return false;
  return (await getDoc(doc(collectionRef, id))).exists();
}

/**
 * Writes `body` at `body.id`, or throws `DocumentIdTakenError` if that document
 * already exists.
 *
 * Stamps both timestamps: a created record is new, so there is no earlier
 * `createdAt` to preserve. That is also what lets `firestore.rules` tell a
 * create from an edit — an update may not change `createdAt`, so a write that
 * carries a fresh one over an existing record is refused by the rules as well as
 * by the transaction.
 */
export async function createDocument<T extends { id: string }>(
  collectionRef: CollectionReference<DocumentData>,
  body: T,
): Promise<T> {
  const now = new Date().toISOString();
  const toSave = { ...body, createdAt: now, updatedAt: now };
  const ref = doc(collectionRef, body.id);

  await runTransaction(db, async (tx) => {
    const existing = await tx.get(ref);
    if (existing.exists()) throw new DocumentIdTakenError(body.id);
    tx.set(ref, toSave);
  });

  return toSave;
}
